import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    BooleanInput,
    Create,
    Edit,
    FormDataConsumer,
    List,
    NumberInput,
    SelectInput,
    SimpleForm,
    TextInput,
    required,
    useDataProvider,
    useNotify,
    useRecordContext,
    useRedirect,
    useListContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Paper,
    Stack,
    TextField as MuiTextField,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import QuizRoundedIcon from '@mui/icons-material/QuizRounded';
import { ImageCropDialog } from './components/ImageCropDialog';
import { QuestSectionTabs } from './components/QuestSectionTabs';
import { uploadQuestTaskImage, deleteQuestTaskImage } from './s3Service';

// Универсальный движок задач: сканирование животного или викторина (ответы на вопросы).
const QUEST_TYPES = [
    { id: 'scan_animal', name: 'Сканировать животное' },
    { id: 'quiz', name: 'Ответ на вопрос' },
];

const typeName = (id) => (QUEST_TYPES.find((t) => t.id === id)?.name || id);

// Карточка задачи в приложении — 88×88 pt со скруглением 14 (как в списке животных),
// поэтому кроп квадратный, экспорт 440×440 (5×), маска предпросмотра 14/88 ≈ 15.9%.
export const CARD_PREVIEW_RADIUS = '15.9%';
const TASK_IMAGE_OUTPUT = 440;

const clampInt = (value, min, max) => {
    const n = Math.trunc(Number(value));
    if (Number.isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
};

// ── Картинка задачи: одна, необязательная, через кроппер в S3 (service/quest) ──
const QuestImageInput = () => {
    const { setValue } = useFormContext();
    const imageUrl = useWatch({ name: 'image_url' });
    const imageFile = useWatch({ name: '__image_file' });
    const [rawFile, setRawFile] = useState(null);
    const [cropOpen, setCropOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Ключ картинки задачи в S3 фиксированный (task_id_<id>.png), поэтому после
    // перезаписи URL не меняется и браузер показал бы старый кадр из кэша.
    // Метку добавляем только к превью — в базу она не уходит.
    const [cacheBust, setCacheBust] = useState('');
    const notify = useNotify();
    const dataProvider = useDataProvider();
    // В форме создания id ещё нет — там картинка уходит после «Сохранить».
    const record = useRecordContext();
    const isEdit = Boolean(record?.id);

    const previewUrl = useMemo(
        () => (imageFile ? URL.createObjectURL(imageFile) : null),
        [imageFile]
    );
    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const shownUrl = previewUrl || (imageUrl ? `${imageUrl}${cacheBust}` : null);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setRawFile(file);
        setCropOpen(true);
    };

    const handleCropApply = async (croppedFile, error) => {
        setCropOpen(false);
        setRawFile(null);
        if (error || !croppedFile) {
            notify(error?.message || 'Не удалось обрезать изображение', { type: 'error' });
            return;
        }
        if (!isEdit) {
            setValue('__image_file', croppedFile, { shouldDirty: true });
            return;
        }

        setUploading(true);
        try {
            const url = await uploadQuestTaskImage(croppedFile, record.id);
            // Патчим серверными полями: незасейвленные правки формы — не наше дело,
            // сохраняем только картинку. Вопросы квиза уходят те же, что на сервере.
            await dataProvider.update('quests', {
                id: record.id,
                data: { ...record, image_url: url },
                previousData: record,
            });
            setValue('__image_file', null, { shouldDirty: false });
            setValue('image_url', url, { shouldDirty: false });
            setCacheBust(`?v=${Date.now()}`);
            notify('Изображение загружено и сохранено', { type: 'success' });
        } catch (uploadError) {
            notify(`Не удалось сохранить изображение: ${uploadError.message || ''}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        if (!isEdit) {
            setValue('__image_file', null, { shouldDirty: true });
            setValue('image_url', null, { shouldDirty: true });
            return;
        }
        if (!window.confirm('Удалить изображение задачи? Оно сразу пропадёт из приложения, отменить будет нельзя.')) {
            return;
        }

        setDeleting(true);
        try {
            // Порядок как у наград: сперва ссылка на бэкенде, потом файл в S3.
            await dataProvider.update('quests', {
                id: record.id,
                data: { ...record, image_url: null },
                previousData: record,
            });
            setValue('__image_file', null, { shouldDirty: false });
            setValue('image_url', null, { shouldDirty: false });
            setCacheBust('');
            try {
                await deleteQuestTaskImage(record.id);
            } catch (s3Error) {
                notify('Картинка убрана из задачи, но файл в S3 удалить не удалось', { type: 'warning' });
                return;
            }
            notify('Изображение удалено', { type: 'success' });
        } catch (deleteError) {
            notify(`Не удалось удалить изображение: ${deleteError.message || ''}`, { type: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Изображение задачи (необязательно)
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
                <Box
                    sx={{
                        width: 120,
                        height: 120,
                        borderRadius: CARD_PREVIEW_RADIUS,
                        overflow: 'hidden',
                        backgroundColor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    {shownUrl ? (
                        <img
                            src={shownUrl}
                            alt="Изображение задачи"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <AddPhotoAlternateIcon color="disabled" fontSize="large" />
                    )}
                </Box>
                <Stack spacing={1}>
                    <Button
                        variant="outlined"
                        component="label"
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
                    >
                        {uploading ? 'Загрузка…' : (shownUrl ? 'Заменить' : 'Загрузить')}
                        <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                    </Button>
                    {shownUrl && (
                        <Button
                            color="error"
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
                            onClick={handleRemove}
                        >
                            {deleting ? 'Удаление…' : 'Удалить'}
                        </Button>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        В приложении показывается карточкой 88×88 со скруглением
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {isEdit
                            ? 'Загрузка и удаление применяются сразу, без «Сохранить».'
                            : 'Картинка загрузится после создания задачи.'}
                    </Typography>
                </Stack>
            </Stack>

            <ImageCropDialog
                open={cropOpen}
                file={rawFile}
                title="Обрезка изображения задачи"
                aspectRatio={1}
                outputWidth={TASK_IMAGE_OUTPUT}
                outputHeight={TASK_IMAGE_OUTPUT}
                previewBorderRadius={CARD_PREVIEW_RADIUS}
                previewMaxWidth={420}
                helperText="Выберите масштаб и положение — так картинка будет выглядеть в приложении"
                onCancel={() => {
                    setCropOpen(false);
                    setRawFile(null);
                }}
                onApply={handleCropApply}
            />
        </Box>
    );
};

// ── Конструктор вопросов квиза ─────────────────────────────────────────────────
const MAX_QUESTIONS = 30;
const MAX_OPTIONS = 10;

const emptyOption = () => ({ text: '', is_correct: false });
const emptyQuestion = () => ({ text: '', options: [emptyOption(), emptyOption()] });

const resizeArray = (items, count, makeEmpty) => {
    const next = items.slice(0, count);
    while (next.length < count) next.push(makeEmpty());
    return next;
};

const QuizQuestionsInput = () => {
    const { setValue } = useFormContext();
    const questions = useWatch({ name: 'questions' }) || [];

    // Пустой квиз сразу получает один вопрос-заготовку
    useEffect(() => {
        if (!questions.length) {
            setValue('questions', [emptyQuestion()], { shouldDirty: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const update = (next) => setValue('questions', next, { shouldDirty: true });

    const setQuestionCount = (value) => {
        update(resizeArray(questions, clampInt(value, 1, MAX_QUESTIONS), emptyQuestion));
    };

    const patchQuestion = (index, patch) => {
        update(questions.map((q, i) => (i === index ? { ...q, ...patch } : q)));
    };

    const setOptionCount = (index, value) => {
        const question = questions[index];
        patchQuestion(index, {
            options: resizeArray(question.options || [], clampInt(value, 2, MAX_OPTIONS), emptyOption),
        });
    };

    const patchOption = (qIndex, oIndex, patch) => {
        const question = questions[qIndex];
        patchQuestion(qIndex, {
            options: (question.options || []).map((o, i) => (i === oIndex ? { ...o, ...patch } : o)),
        });
    };

    // Правильный вариант ровно один — включение чекбокса снимает отметку с остальных
    const markCorrect = (qIndex, oIndex) => {
        const question = questions[qIndex];
        patchQuestion(qIndex, {
            options: (question.options || []).map((o, i) => ({ ...o, is_correct: i === oIndex })),
        });
    };

    return (
        <Box sx={{ width: '100%', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Вопросы квиза
            </Typography>
            <MuiTextField
                type="number"
                size="small"
                label="Количество вопросов"
                value={questions.length || 1}
                onChange={(e) => setQuestionCount(e.target.value)}
                inputProps={{ min: 1, max: MAX_QUESTIONS }}
                sx={{ mb: 2, width: 220 }}
            />

            <Stack spacing={2}>
                {questions.map((question, qIndex) => {
                    const options = question.options || [];
                    const hasCorrect = options.some((o) => o.is_correct);
                    return (
                        <Card key={qIndex} variant="outlined">
                            <CardContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle2">Вопрос {qIndex + 1}</Typography>
                                    <MuiTextField
                                        fullWidth
                                        size="small"
                                        label="Текст вопроса"
                                        value={question.text || ''}
                                        onChange={(e) => patchQuestion(qIndex, { text: e.target.value })}
                                        error={!question.text}
                                        helperText={!question.text ? 'Введите текст вопроса' : undefined}
                                    />
                                    <MuiTextField
                                        type="number"
                                        size="small"
                                        label="Количество вариантов ответа"
                                        value={options.length}
                                        onChange={(e) => setOptionCount(qIndex, e.target.value)}
                                        inputProps={{ min: 2, max: MAX_OPTIONS }}
                                        sx={{ width: 260 }}
                                    />
                                    <Stack spacing={1}>
                                        {options.map((option, oIndex) => (
                                            <Stack key={oIndex} direction="row" spacing={1} alignItems="center">
                                                <MuiTextField
                                                    fullWidth
                                                    size="small"
                                                    label={`Вариант ${oIndex + 1}`}
                                                    value={option.text || ''}
                                                    onChange={(e) => patchOption(qIndex, oIndex, { text: e.target.value })}
                                                    error={!option.text}
                                                />
                                                <FormControlLabel
                                                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                                    control={(
                                                        <Checkbox
                                                            checked={!!option.is_correct}
                                                            onChange={() => markCorrect(qIndex, oIndex)}
                                                        />
                                                    )}
                                                    label="Правильный"
                                                />
                                            </Stack>
                                        ))}
                                    </Stack>
                                    {!hasCorrect && (
                                        <Typography variant="caption" color="error">
                                            Отметьте правильный вариант ответа
                                        </Typography>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>
        </Box>
    );
};

// При создании квиз по умолчанию неактивен — включается вручную после проверки вопросов
const QuizCreateDefaults = () => {
    const { setValue } = useFormContext();
    useEffect(() => {
        setValue('is_active', false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
};

const validateQuest = (values) => {
    const errors = {};
    if (values.type === 'quiz') {
        const questions = values.questions || [];
        if (!questions.length) {
            errors.type = 'Добавьте хотя бы один вопрос';
        } else {
            const broken = questions.some((q) => (
                !q.text
                || (q.options || []).length < 2
                || (q.options || []).some((o) => !o.text)
                || (q.options || []).filter((o) => o.is_correct).length !== 1
            ));
            if (broken) {
                errors.type = 'Заполните все вопросы и варианты и отметьте по одному правильному ответу';
            }
        }
    }
    return errors;
};

const QuestForm = ({ isCreate = false }) => (
    <SimpleForm validate={validateQuest} sx={{ '& .RaSimpleForm-stack': { width: '100%', maxWidth: 'none' } }}>
        <Box sx={{ width: '100%', mb: 2 }}>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>Игровой маршрут</Typography>
            <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em' }}>{isCreate ? 'Новая задача' : 'Редактирование задачи'}</Typography>
            <Typography color="text.secondary" sx={{ mt: .75 }}>Настройте механику, награду и то, что увидит посетитель в приложении.</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(300px, 1fr)' }, gap: 3, width: '100%', alignItems: 'start' }}>
            <Box sx={{ display: 'grid', gap: 3 }}>
                <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: .5 }}>Описание задачи</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Название должно объяснять действие без дополнительных подсказок.</Typography>
                    <TextInput source="title" label="Название" validate={required()} fullWidth />
                    <TextInput source="description" label="Описание" multiline minRows={4} fullWidth />
                    <SelectInput source="type" label="Тип задачи" choices={QUEST_TYPES} defaultValue="scan_animal" validate={required()} fullWidth />
                    <FormDataConsumer>
                        {({ formData }) => (formData.type === 'quiz' ? (
                            <>{isCreate && <QuizCreateDefaults />}<QuizQuestionsInput /></>
                        ) : (
                            <NumberInput source="target_animal_id" label="ID животного для сканирования" helperText="ID из раздела «Животные»" min={1} fullWidth />
                        ))}
                    </FormDataConsumer>
                </Paper>
            </Box>
            <Box sx={{ display: 'grid', gap: 3, position: { lg: 'sticky' }, top: { lg: 88 } }}>
                <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Карточка в приложении</Typography>
                    <QuestImageInput />
                </Paper>
                <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Награда и доступность</Typography>
                    <NumberInput source="reward_points" label="Очки за выполнение" min={0} defaultValue={0} fullWidth />
                    <BooleanInput source="is_active" label="Задача активна" defaultValue={!isCreate ? undefined : true} />
                    <Typography variant="caption" color="text.secondary">Неактивная задача сохраняется как черновик и не показывается посетителям.</Typography>
                </Paper>
            </Box>
        </Box>
    </SimpleForm>
);

const QuestCards = () => {
    const { data = [], isPending } = useListContext();
    if (isPending) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
            {data.map((record) => {
                const isQuiz = record.type === 'quiz';
                return (
                    <Paper key={record.id} component="a" href={`#/quests/${record.id}`} elevation={0} sx={{ p: 2.25, border: '1px solid', borderColor: 'divider', borderRadius: 3, color: 'inherit', textDecoration: 'none', transition: '160ms ease', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-3px)', boxShadow: '0 14px 34px rgba(15,23,42,.1)' }, '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 } }}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <Box sx={{ width: 64, height: 64, borderRadius: CARD_PREVIEW_RADIUS, overflow: 'hidden', bgcolor: 'action.hover', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                                {record.image_url ? <Box component="img" src={record.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : isQuiz ? <QuizRoundedIcon color="disabled" /> : <PetsRoundedIcon color="disabled" />}
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}><Chip size="small" label={isQuiz ? 'Квиз' : 'Сканирование'} color={isQuiz ? 'secondary' : 'primary'} variant="outlined" /><Chip size="small" label={record.is_active ? 'Активна' : 'Черновик'} color={record.is_active ? 'success' : 'default'} /></Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, mt: 1.5 }}>{record.title || 'Без названия'}</Typography>
                            </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, minHeight: 40, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{record.description || 'Описание не заполнено'}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}><Typography variant="body2" color="text.secondary">{isQuiz ? `${(record.questions || []).length} вопросов` : `Животное #${record.target_animal_id || '—'}`}</Typography><Chip icon={<EmojiEventsRoundedIcon />} label={`${record.reward_points || 0} очков`} size="small" /></Box>
                    </Paper>
                );
            })}
        </Box>
    );
};

export const QuestList = () => (
    <>
        <QuestSectionTabs />
        <List sort={{ field: 'created_at', order: 'DESC' }} title="Квесты" sx={{ '& .RaList-content': { boxShadow: 'none', bgcolor: 'transparent' } }}>
            <Box sx={{ mb: 3 }}><Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em', mb: .75 }}>Задачи квеста</Typography><Typography color="text.secondary">Сканирование животных и квизы, за которые посетители получают очки.</Typography></Box>
            <QuestCards />
        </List>
    </>
);

// На создании id ещё нет, поэтому картинка грузится в S3 после ответа бэкенда,
// затем задача сразу дополняется ссылкой.
export const QuestCreate = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const pendingFileRef = useRef(null);

    const transform = (data) => {
        const { __image_file, ...rest } = data;
        pendingFileRef.current = __image_file || null;
        return rest;
    };

    const onSuccess = async (created) => {
        const file = pendingFileRef.current;
        pendingFileRef.current = null;
        if (file) {
            try {
                const url = await uploadQuestTaskImage(file, created.id);
                await dataProvider.update('quests', {
                    id: created.id,
                    data: { ...created, image_url: url },
                    previousData: created,
                });
            } catch (error) {
                notify(`Задача создана, но изображение не загрузилось: ${error.message || ''}`, { type: 'warning' });
                redirect('list', 'quests');
                return;
            }
        }
        notify('Задача создана', { type: 'success' });
        redirect('list', 'quests');
    };

    return (
        <>
            <QuestSectionTabs />
            <Create title="Квесты" transform={transform} mutationOptions={{ onSuccess }}>
                <QuestForm isCreate />
            </Create>
        </>
    );
};

// На редактировании id известен: сначала кладём картинку в S3, потом сохраняем задачу.
const editTransform = async (data) => {
    const { __image_file, ...rest } = data;
    if (__image_file) {
        rest.image_url = await uploadQuestTaskImage(__image_file, data.id);
    } else if (!rest.image_url) {
        // Картинку убрали в форме — подчищаем файл в S3 (отсутствующий ключ не ошибка)
        try {
            await deleteQuestTaskImage(data.id);
        } catch (error) {
            console.error('Не удалось удалить изображение задачи из S3:', error);
        }
        rest.image_url = null;
    }
    return rest;
};

export const QuestEdit = () => (
    <>
        <QuestSectionTabs />
        <Edit title="Квесты" transform={editTransform} mutationMode="pessimistic">
            <QuestForm />
        </Edit>
    </>
);

export { typeName };
